import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const STATS = [
  { value: "18+", label: "Years Engineering Bio-Performance" },
  { value: "500", label: "Fortune-Tier Executives Rebuilt" },
  { value: "12", label: "Clients Accepted Per Quarter" },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="about" ref={ref} className="relative bg-ink py-24 sm:py-32 lg:py-40 overflow-hidden" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-center">
        <motion.div
          className="lg:col-span-5 relative"
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
        >
          <div className="absolute -top-4 -left-4 w-full h-full border border-gold/30 pointer-events-none" />
          <div className="relative overflow-hidden bg-ink-3 aspect-[4/5]">
            <motion.img
              style={{ y: imgY, scale: 1.12 }}
              src="/assets/joe-fenix.webp"
              alt="Joe Fenix — Founder and Architect of Somatic Mastery"
              className="w-full h-full object-cover"
              data-testid="about-portrait"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
          </div>
          <p className="mt-5 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Joe Fenix — Architect of Executive Somatic Mastery
          </p>
        </motion.div>

        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
        >
          <p className="text-[11px] uppercase tracking-[0.35em] text-copper-hover mb-5" data-testid="about-eyebrow">
            The Architect
          </p>
          <h2 className="font-serif text-3xl sm:text-5xl text-gold font-medium tracking-tight leading-[1.15] mb-7" data-testid="about-heading">
            Burnout Is Not a Badge.<br />It Is an Engineering Failure.
          </h2>
          <div className="space-y-5 text-sm sm:text-base text-neutral-400 font-light leading-relaxed max-w-2xl">
            <p>
              Joe Fenix has spent over 18 years engineering peak bio-performance
              protocols for Fortune 500 CEOs, hedge fund managers, elite
              founders, and tech visionaries — leaders who cannot afford a
              single depleted afternoon.
            </p>
            <p>
              By combining molecular biology, neuro-somatic regulation, and
              executive stress thermodynamics, the Fenix Method replaces
              burnout with sustainable cellular voltage. Not motivation. Not
              willpower. <span className="text-gold-soft">Architecture.</span>
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6 sm:gap-10 mt-12 max-w-2xl">
            {STATS.map((s, i) => (
              <div key={i} className="border-t border-gold/25 pt-5" data-testid={`about-stat-${i + 1}`}>
                <p className="font-serif text-3xl sm:text-5xl text-gold leading-none">{s.value}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-neutral-500 mt-3 leading-relaxed">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
